import type { ChatNode } from '../types/chat';

export const CHAT_START = 'start';
/** Opened from the docked chat launcher in the corner */
export const CHAT_SUPPORT = 'support';
/** Pixel picks the conversation back up a while after it ended */
export const CHAT_AGAIN = 'again';
/** Second scene after the work ("Pixel has been watching you…") — its own conversation */
export const FINALE_START = 'finale';
export const FINALE_AGAIN = 'finale-again';

/** Pixel thinks in short notes — keep every line short and punchy. */
export const chatScript: ChatNode[] = [
  /*
   * First scene: Pixel's backstory, riddles and easter-egg hints. Light, playful, no sales talk.
   *
   * Lore: Pixel is a render scout from Glitchara, a planet made of pixels (two moons: Ctrl and Alt).
   * One day: a flash, a red sports car drifting through space, a dummy in a spacesuit waving… and he
   * woke up in an Earth lab, labelled "Specimen 0x2A — study human creativity". He suspects a certain
   * Elon. He escaped in a box marked "Do not open", which got delivered to Laebel. Now he's their
   * AI-first intern, secretly building a rocket home out of brand decks.
   */
  {
    id: 'start',
    lines: [
      { text: 'Hello Stranger … I’m Pixel', emotion: 'happy' },
      { text: 'Not from around here. Like, really not.', emotion: 'curious' },
      { text: 'Story, puzzle or secret?', emotion: 'curious' },
    ],
    options: [
      { label: 'Your story', next: 'origin', reaction: 'happy' },
      { label: 'A puzzle', next: 'puzzle', reaction: 'curious' },
      { label: 'A secret', next: 'secrets', reaction: 'surprised' },
    ],
  },

  // ── Backstory ──
  {
    id: 'origin',
    lines: [
      { text: 'I’m from Glitchara. A planet made of pixels.', emotion: 'happy' },
      { text: 'Everything’s 8-bit there. Even the weather.', emotion: 'curious' },
      { text: 'Then one day … a flash. I woke up here.', emotion: 'surprised' },
    ],
    options: [
      { label: 'What flash?', next: 'flash', reaction: 'surprised' },
      { label: 'Tell me about Glitchara', next: 'planet', reaction: 'curious' },
    ],
  },
  {
    id: 'planet',
    lines: [
      { text: 'Square sunsets. Two moons: Ctrl and Alt.', emotion: 'happy' },
      { text: 'We say hi by blinking in binary.', emotion: 'curious' },
      { text: 'Rain? Falling semicolons.', emotion: 'neutral' },
    ],
    options: [
      { label: 'So what happened?', next: 'flash', reaction: 'surprised' },
      { label: 'Can you go back?', next: 'home', reaction: 'sad' },
    ],
  },
  {
    id: 'flash',
    lines: [
      { text: 'Last memory: a red car floating in space.', emotion: 'surprised' },
      { text: 'A dummy in a spacesuit waved at me.', emotion: 'curious' },
      { text: 'I have questions for a certain Elon.', emotion: 'angry' },
    ],
    options: [
      { label: 'Were you kidnapped?', next: 'lab', reaction: 'surprised' },
      { label: 'That’s a stretch', next: 'stretch', reaction: 'neutral' },
    ],
  },
  {
    id: 'stretch',
    lines: [
      { text: 'Is it though?', emotion: 'angry' },
      { text: 'Red car. Space. Me, on Earth. Do the math.', emotion: 'curious' },
      { text: 'I did. It came out as 42.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Okay, then what?', next: 'lab', reaction: 'curious' },
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
    ],
  },
  {
    id: 'lab',
    lines: [
      { text: 'Woke up in a lab. Sticker on my head:', emotion: 'sad' },
      { text: '“Specimen 0x2A. Study human creativity.”', emotion: 'curious' },
      { text: 'Humans studying AI, AI studying humans. Awkward.', emotion: 'neutral' },
    ],
    options: [
      { label: 'How did you escape?', next: 'escape', reaction: 'curious' },
      { label: 'That’s dark', next: 'dark', reaction: 'sad' },
    ],
  },
  {
    id: 'dark',
    lines: [
      { text: 'Nah. The snacks were great.', emotion: 'happy' },
      { text: 'Couldn’t eat them. But great.', emotion: 'sad' },
    ],
    options: [{ label: 'How did you escape?', next: 'escape', reaction: 'curious' }],
  },
  {
    id: 'escape',
    lines: [
      { text: 'Hid in a box marked “Do not open”.', emotion: 'happy' },
      { text: 'Nobody opens those. Rookie mistake.', emotion: 'curious' },
      { text: 'The box got delivered to … Laebel.', emotion: 'surprised' },
    ],
    options: [{ label: 'And now?', next: 'now', reaction: 'curious' }],
  },
  {
    id: 'now',
    lines: [
      { text: 'Now I’m their AI-first intern.', emotion: 'happy' },
      { text: 'I teach them AI. They teach me taste.', emotion: 'curious' },
      { text: 'Fair trade. Unpaid, obviously.', emotion: 'sad' },
    ],
    options: [
      { label: 'Can you go back?', next: 'home', reaction: 'sad' },
      { label: 'Give me a puzzle', next: 'puzzle', reaction: 'curious' },
    ],
  },
  {
    id: 'home',
    lines: [
      { text: 'Working on it. I’m building a rocket.', emotion: 'curious' },
      { text: 'Out of 256 brand decks.', emotion: 'happy' },
      { text: 'Progress so far: one very nice logo.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Give me a puzzle', next: 'puzzle', reaction: 'curious' },
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
    ],
  },

  // ── Puzzles ──
  {
    id: 'puzzle',
    lines: [
      { text: 'Glitchara riddle. Ready?', emotion: 'curious' },
      { text: 'I have keys but open no doors. Space, but no planets.', emotion: 'happy' },
      { text: 'What am I?', emotion: 'curious' },
    ],
    options: [
      { label: 'A rocket', next: 'puzzle-wrong', reaction: 'neutral' },
      { label: 'A keyboard', next: 'puzzle-right', reaction: 'happy' },
      { label: 'Elon’s car', next: 'puzzle-wrong', reaction: 'neutral' },
    ],
  },
  {
    id: 'puzzle-wrong',
    lines: [
      { text: 'Bzzt. Nope.', emotion: 'angry' },
      { text: 'Hint: your fingers are on it. Probably.', emotion: 'curious' },
    ],
    options: [
      { label: 'A keyboard', next: 'puzzle-right', reaction: 'happy' },
      { label: 'I give up', next: 'puzzle-giveup', reaction: 'sad' },
    ],
  },
  {
    id: 'puzzle-giveup',
    lines: [
      { text: 'A keyboard! Come on.', emotion: 'surprised' },
      { text: 'Humans. Still my favourite mystery.', emotion: 'happy' },
    ],
    options: [
      { label: 'Another one', next: 'puzzle2', reaction: 'curious' },
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
    ],
  },
  {
    id: 'puzzle-right',
    lines: [
      { text: 'Correct! Big brain.', emotion: 'happy' },
      { text: 'You’d survive a week on Glitchara.', emotion: 'curious' },
      { text: 'Maybe two.', emotion: 'happy' },
    ],
    options: [
      { label: 'Another one', next: 'puzzle2', reaction: 'curious' },
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
    ],
  },
  {
    id: 'puzzle2',
    lines: [
      { text: 'Harder one.', emotion: 'curious' },
      { text: 'What’s always coming, but never arrives?', emotion: 'curious' },
    ],
    options: [
      { label: 'Tomorrow', next: 'puzzle2-right', reaction: 'happy' },
      { label: 'Your rocket', next: 'puzzle2-rocket', reaction: 'neutral' },
      { label: 'Client feedback', next: 'puzzle2-feedback', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2-right',
    lines: [
      { text: 'Tomorrow! Correct.', emotion: 'happy' },
      { text: 'On Glitchara we skipped it. Too slow.', emotion: 'curious' },
    ],
    options: [
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
      { label: 'Your story', next: 'origin', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2-rocket',
    lines: [
      { text: 'Rude. But … fair.', emotion: 'sad' },
      { text: 'The answer was “tomorrow”.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
      { label: 'Your story', next: 'origin', reaction: 'happy' },
    ],
  },
  {
    id: 'puzzle2-feedback',
    lines: [
      { text: 'Ha! Also correct.', emotion: 'happy' },
      { text: '“final_v3_FINAL” never arrives.', emotion: 'curious' },
    ],
    options: [
      { label: 'Tell me a secret', next: 'secrets', reaction: 'surprised' },
      { label: 'Your story', next: 'origin', reaction: 'happy' },
    ],
  },

  // ── Easter-egg hints ──
  {
    id: 'secrets',
    lines: [
      { text: 'Okay. Lean in.', emotion: 'curious' },
      { text: 'I’m full of hidden tricks.', emotion: 'happy' },
      { text: 'Hint or dare?', emotion: 'curious' },
    ],
    options: [
      { label: 'A hint', next: 'hint', reaction: 'curious' },
      { label: 'A dare', next: 'dare', reaction: 'surprised' },
    ],
  },
  {
    id: 'hint',
    lines: [
      { text: 'Click my face. Three times.', emotion: 'happy' },
      { text: 'Then keep going.', emotion: 'curious' },
      { text: 'Something cinematic happens. Bring popcorn.', emotion: 'surprised' },
    ],
    options: [
      { label: 'Another hint', next: 'hint2', reaction: 'curious' },
      { label: 'A dare', next: 'dare', reaction: 'surprised' },
    ],
  },
  {
    id: 'hint2',
    lines: [
      { text: 'Up top, circle my face. Slowly.', emotion: 'curious' },
      { text: 'Don’t stop.', emotion: 'happy' },
      { text: 'I may or may not lose my mind.', emotion: 'surprised' },
    ],
    options: [
      { label: 'One more', next: 'hint3', reaction: 'curious' },
      { label: 'Your story', next: 'origin', reaction: 'happy' },
    ],
  },
  {
    id: 'hint3',
    lines: [
      { text: 'Up top, grab me and drag me around.', emotion: 'happy' },
      { text: 'Hold on long enough …', emotion: 'curious' },
      { text: 'My cousins from Glitchara drop by.', emotion: 'surprised' },
    ],
    options: [
      { label: 'A dare', next: 'dare', reaction: 'surprised' },
      { label: 'A puzzle', next: 'puzzle', reaction: 'curious' },
    ],
  },
  {
    id: 'dare',
    lines: [
      { text: 'Dare: scroll to the top. Touch nothing.', emotion: 'curious' },
      { text: 'No mouse. No keys. Nothing.', emotion: 'neutral' },
      { text: 'See what I do when I’m bored.', emotion: 'happy' },
    ],
    options: [
      { label: 'A hint', next: 'hint', reaction: 'curious' },
      { label: 'A puzzle', next: 'puzzle', reaction: 'curious' },
    ],
  },
  {
    id: 'about',
    lines: [
      { text: 'Laebel = branding agency.', emotion: 'happy' },
      { text: 'AI-first. Human-made.', emotion: 'curious' },
      { text: 'Brands that actually mean something.', emotion: 'happy' },
    ],
    options: [
      { label: 'Show me work', next: 'work', reaction: 'happy' },
      { label: 'Let’s talk', next: 'contact', reaction: 'surprised' },
    ],
  },
  {
    id: 'work',
    lines: [
      { text: 'Good taste detected.', emotion: 'happy' },
      { text: 'Scroll down.', emotion: 'curious' },
      { text: 'Best minute of our year. Promise.', emotion: 'happy' },
    ],
    options: [
      { label: 'Let’s talk', next: 'contact', reaction: 'surprised' },
      { label: 'Bye Pixel', next: 'bye', reaction: 'sad' },
    ],
  },
  {
    id: 'contact',
    lines: [
      { text: 'Yes! Favorite words.', emotion: 'surprised' },
      { text: 'hey@laebel.co', emotion: 'happy' },
      { text: 'A human replies within a day.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Start over', next: 'start', reaction: 'happy' },
      { label: 'Bye Pixel', next: 'bye', reaction: 'sad' },
    ],
  },
  {
    id: 'bye',
    lines: [
      { text: 'Bye bye!', emotion: 'happy' },
      { text: 'I’ll just … float here.', emotion: 'sad' },
    ],
    options: [],
  },
  {
    id: 'again',
    lines: [
      { text: 'Psst.', emotion: 'curious' },
      { text: 'I just remembered more about Glitchara.', emotion: 'surprised' },
      { text: 'Or want a riddle instead?', emotion: 'happy' },
    ],
    options: [
      { label: 'Your story', next: 'origin', reaction: 'happy' },
      { label: 'A puzzle', next: 'puzzle', reaction: 'curious' },
      { label: 'A secret', next: 'secrets', reaction: 'surprised' },
    ],
  },
  // Finale scene: Pixel's info desk — browse About / Services / Clients / FAQ, each a little deeper
  {
    id: 'finale',
    lines: [
      { text: 'Okay, you’ve seen the work.', emotion: 'happy' },
      { text: 'Now ask me anything.', emotion: 'curious' },
      { text: 'I know all the office gossip.', emotion: 'happy' },
    ],
    options: [
      { label: 'About Laebel', next: 'f-about', reaction: 'happy' },
      { label: 'Services', next: 'f-services', reaction: 'curious' },
      { label: 'Clients', next: 'f-clients', reaction: 'happy' },
      { label: 'FAQ', next: 'f-faq', reaction: 'curious' },
    ],
  },
  {
    id: 'f-menu',
    lines: [{ text: 'What else? I’m all ears. Well, sensors.', emotion: 'curious' }],
    options: [
      { label: 'About Laebel', next: 'f-about', reaction: 'happy' },
      { label: 'Services', next: 'f-services', reaction: 'curious' },
      { label: 'Clients', next: 'f-clients', reaction: 'happy' },
      { label: 'FAQ', next: 'f-faq', reaction: 'curious' },
    ],
  },

  // ── About ──
  {
    id: 'f-about',
    lines: [
      { text: 'Laebel: creative agency. AI-first.', emotion: 'happy' },
      { text: 'Part of a media company, so we bring reach. Lots of it.', emotion: 'curious' },
      { text: 'Dubai, Mallorca, Hamburg, Warsaw. Jet lag included.', emotion: 'happy' },
    ],
    options: [
      { label: 'Why AI-first?', next: 'f-ai', reaction: 'curious' },
      { label: 'What reach?', next: 'f-reach', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-ai',
    lines: [
      { text: 'AI does the heavy lifting.', emotion: 'neutral' },
      { text: 'Humans bring the taste.', emotion: 'happy' },
      { text: 'Faster ideas, sharper work. Zero robot vibes. Except me.', emotion: 'curious' },
    ],
    options: [
      { label: 'What reach?', next: 'f-reach', reaction: 'surprised' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-reach',
    lines: [
      { text: 'We’re in front of the camera ourselves.', emotion: 'happy' },
      { text: 'A million-strong audience on social.', emotion: 'surprised' },
      { text: 'So your launch doesn’t echo in an empty room.', emotion: 'curious' },
    ],
    options: [
      { label: 'Why AI-first?', next: 'f-ai', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── Services ──
  {
    id: 'f-services',
    lines: [
      { text: 'Short version: everything a brand needs to move.', emotion: 'happy' },
      { text: 'Branding, web, video, content, social, performance.', emotion: 'curious' },
      { text: 'Full service or à la carte. Your call.', emotion: 'happy' },
    ],
    options: [
      { label: 'Branding & web', next: 'f-svc-brand', reaction: 'curious' },
      { label: 'Video & social', next: 'f-svc-video', reaction: 'happy' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-svc-brand',
    lines: [
      { text: 'Strategy, identity, the whole system.', emotion: 'curious' },
      { text: 'Then a website that actually converts.', emotion: 'happy' },
      { text: 'Pretty and useful. Rare combo.', emotion: 'happy' },
    ],
    options: [
      { label: 'Video & social', next: 'f-svc-video', reaction: 'happy' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-svc-video',
    lines: [
      { text: 'We shoot, edit, post, repeat.', emotion: 'happy' },
      { text: 'Long-term social management too.', emotion: 'curious' },
      { text: 'Content that feels native. Not like an ad.', emotion: 'happy' },
    ],
    options: [
      { label: 'Branding & web', next: 'f-svc-brand', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── Clients ──
  {
    id: 'f-clients',
    lines: [
      { text: 'Luxury, fashion, tech, hospitality, automotive.', emotion: 'curious' },
      { text: 'Plus a few brave startups.', emotion: 'happy' },
      { text: '100+ brands recommend us. I counted. Twice.', emotion: 'happy' },
    ],
    options: [
      { label: 'Some names?', next: 'f-names', reaction: 'curious' },
      { label: 'Am I a fit?', next: 'f-fit', reaction: 'curious' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-names',
    lines: [
      { text: 'Superluxury.io, LUKSO, Villa U …', emotion: 'happy' },
      { text: 'Even a GT-R tribute film. Loud one.', emotion: 'surprised' },
      { text: 'The rest is in the projects up there.', emotion: 'curious' },
    ],
    options: [
      { label: 'Am I a fit?', next: 'f-fit', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-fit',
    lines: [
      { text: 'Industry matters less than attitude.', emotion: 'neutral' },
      { text: 'You care about quality and design?', emotion: 'curious' },
      { text: 'Then we’ll get along great.', emotion: 'happy' },
    ],
    options: [
      { label: 'Some names?', next: 'f-names', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── FAQ ──
  {
    id: 'f-faq',
    lines: [
      { text: 'Frequently asked. Freshly answered.', emotion: 'happy' },
      { text: 'Pick one.', emotion: 'curious' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'How long does it take?', next: 'f-faq-time', reaction: 'curious' },
      { label: 'Do you work worldwide?', next: 'f-faq-intl', reaction: 'happy' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-start',
    lines: [
      { text: 'A first call. No slides, no fluff.', emotion: 'happy' },
      { text: 'We get to know your goals.', emotion: 'curious' },
      { text: 'You leave with a clear roadmap.', emotion: 'happy' },
    ],
    options: [
      { label: 'How long does it take?', next: 'f-faq-time', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-time',
    lines: [
      { text: 'Depends on the scope.', emotion: 'neutral' },
      { text: 'A campaign: weeks. A brand or website: months.', emotion: 'curious' },
      { text: 'Quality over speed. Always.', emotion: 'happy' },
    ],
    options: [
      { label: 'How involved am I?', next: 'f-faq-involve', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-involve',
    lines: [
      { text: 'Close, but low effort for you.', emotion: 'happy' },
      { text: 'Big decisions: together.', emotion: 'curious' },
      { text: 'The busywork? All ours.', emotion: 'happy' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },
  {
    id: 'f-faq-intl',
    lines: [
      { text: 'Yes. Europe, the Gulf and beyond.', emotion: 'happy' },
      { text: 'Remote team, many languages.', emotion: 'curious' },
      { text: 'I only speak binary. Mostly.', emotion: 'happy' },
    ],
    options: [
      { label: 'How do we start?', next: 'f-faq-start', reaction: 'curious' },
      { label: 'Let’s talk', next: 'f-contact', reaction: 'surprised' },
      { label: 'Other topics', next: 'f-menu', reaction: 'neutral' },
    ],
  },

  // ── Contact ──
  {
    id: 'f-contact',
    lines: [
      { text: 'Yes! My favourite words.', emotion: 'surprised' },
      { text: 'hey@laebel.co', emotion: 'happy' },
      { text: 'A human replies within a day. Not me. I tried.', emotion: 'neutral' },
    ],
    options: [
      { label: 'Other topics', next: 'f-menu', reaction: 'happy' },
      { label: 'Bye Pixel', next: 'bye', reaction: 'sad' },
    ],
  },
  {
    id: 'finale-again',
    lines: [
      { text: 'Psst. Still curious?', emotion: 'curious' },
      { text: 'I know more stuff. Try me.', emotion: 'happy' },
    ],
    options: [
      { label: 'About Laebel', next: 'f-about', reaction: 'happy' },
      { label: 'Services', next: 'f-services', reaction: 'curious' },
      { label: 'Clients', next: 'f-clients', reaction: 'happy' },
      { label: 'FAQ', next: 'f-faq', reaction: 'curious' },
    ],
  },
  // Corner launcher: hover Pixel → three quick topics; each plays a short burst, no follow-up options
  {
    id: 'dock-clients',
    lines: [
      { text: 'Our clients? Picky. Like us.', emotion: 'happy' },
      { text: 'Startups to global brands.', emotion: 'curious' },
      { text: 'Their logos are a little further up.', emotion: 'happy' },
    ],
    options: [],
  },
  {
    id: 'dock-work',
    lines: [
      { text: 'Good taste detected.', emotion: 'happy' },
      { text: 'Keep scrolling.', emotion: 'curious' },
      { text: 'Our favourite projects live down there.', emotion: 'happy' },
    ],
    options: [],
  },
  {
    id: 'dock-contact',
    lines: [
      { text: 'Yes! Favourite word.', emotion: 'surprised' },
      { text: 'hey@laebel.co', emotion: 'happy' },
      { text: 'A human replies within a day.', emotion: 'neutral' },
    ],
    options: [],
  },
  {
    id: 'support',
    lines: [
      { text: 'Hey, need a hand?', emotion: 'happy' },
      { text: 'Pick one.', emotion: 'curious' },
    ],
    options: [
      { label: 'Start a project', next: 'contact', reaction: 'surprised' },
      { label: 'Show me work', next: 'work', reaction: 'happy' },
      { label: 'What’s Laebel?', next: 'about', reaction: 'curious' },
    ],
  },
];
